import AttachFileIcon from '@mui/icons-material/AttachFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography
} from '@mui/material';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BugReportPrefill } from '@/shared/components/widgets/BugReporter/BugReporter.types';

import { helpdeskApiService } from '../../services/helpdesk-api.service';
import { CreateTicketPayload, TicketAttachment, TicketCategory, TicketPriority } from '../../types';

interface NewTicketFormProps {
  onSubmit: (data: CreateTicketPayload) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  prefill?: BugReportPrefill;
}

interface CategoryOption {
  value: TicketCategory;
  emoji: string;
  desc: string;
}

interface PriorityOption {
  value: TicketPriority;
  emoji: string;
  suffix: string;
}

const CATEGORIES: CategoryOption[] = [
  { value: TicketCategory.BUG, emoji: '🐛', desc: 'something is broken' },
  { value: TicketCategory.ACCESS, emoji: '🔑', desc: 'need permissions' },
  { value: TicketCategory.INFRASTRUCTURE, emoji: '🏗', desc: 'server / infra issue' },
  { value: TicketCategory.FEATURE, emoji: '✨', desc: 'new functionality' },
  { value: TicketCategory.OTHER, emoji: '🔧', desc: 'something else' }
];

const PRIORITIES: PriorityOption[] = [
  { value: TicketPriority.CRITICAL, emoji: '🔥', suffix: ' — blocking my work now' },
  { value: TicketPriority.HIGH, emoji: '⬆️', suffix: ' — urgent but not blocking' },
  { value: TicketPriority.MEDIUM, emoji: '🟡', suffix: ' — can wait a day or two' },
  { value: TicketPriority.LOW, emoji: '⬇️', suffix: ' — when you have time' }
];

const TIP_KEYS = ['tip1', 'tip2', 'tip3', 'tip4'] as const;

const NewTicketForm: React.FC<NewTicketFormProps> = ({
  onSubmit,
  onCancel,
  isSubmitting,
  prefill
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState(prefill?.title ?? '');
  const [description, setDescription] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState(prefill?.stepsToReproduce ?? '');
  const [category, setCategory] = useState<TicketCategory>(prefill?.category ?? TicketCategory.BUG);
  const [priority, setPriority] = useState<TicketPriority>(
    prefill?.priority ?? TicketPriority.CRITICAL
  );
  const [attachments, setAttachments] = useState<TicketAttachment[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>(() =>
    prefill?.screenshotFile ? [prefill.screenshotFile] : []
  );
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [polishingDescription, setPolishingDescription] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      setPendingFiles((prev) => [...prev, file]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) setPendingFiles((prev) => [...prev, file]);
  };

  const handleRemoveAttachment = (url: string): void => {
    setAttachments((prev) => prev.filter((a) => a.url !== url));
  };

  const handleRemovePendingFile = (idx: number): void => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setUploadError(null);
    let uploaded: TicketAttachment[] = [];
    if (pendingFiles.length > 0) {
      setUploadingFile(true);
      try {
        uploaded = await Promise.all(
          pendingFiles.map(async (file) => {
            const { uploadUrl, publicUrl } = await helpdeskApiService.presignUrl(
              file.name,
              file.type
            );
            const s3Response = await fetch(uploadUrl, {
              method: 'PUT',
              body: file,
              headers: { 'Content-Type': file.type }
            });
            if (!s3Response.ok) throw new Error('S3 upload failed');
            return { url: publicUrl, name: file.name, mimeType: file.type, sizeBytes: file.size };
          })
        );
      } catch {
        setUploadError(t('helpdesk.form.uploadError'));
        setUploadingFile(false);
        return;
      }
      setUploadingFile(false);
    }
    const allAttachments = [...attachments, ...uploaded];
    onSubmit({
      title,
      description,
      stepsToReproduce: stepsToReproduce || undefined,
      category,
      priority,
      attachments: allAttachments.length ? allAttachments : undefined
    });
  };

  const handlePolishDescription = async (): Promise<void> => {
    if (!description.trim()) return;
    setPolishingDescription(true);
    try {
      const { body } = await helpdeskApiService.polishDescription(description);
      setDescription(body);
    } finally {
      setPolishingDescription(false);
    }
  };

  const handleCategoryChange = (e: SelectChangeEvent): void =>
    setCategory(e.target.value as TicketCategory);
  const handlePriorityChange = (e: SelectChangeEvent): void =>
    setPriority(e.target.value as TicketPriority);

  const isValid = title.trim().length > 0 && description.trim().length > 0;

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={3} alignItems="flex-start">
        {/* ── Left: Form card ── */}
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              bgcolor: '#FFFFFF',
              borderRadius: '16px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              p: { xs: 3, md: 4 }
            }}
          >
            {/* Title */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#001048', mb: 0.75 }}>
                {t('helpdesk.form.title')} <span style={{ color: '#E53935' }}>*</span>
              </Typography>
              <TextField
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                fullWidth
                disabled={isSubmitting}
                placeholder={t('helpdesk.form.titlePlaceholder')}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '14px' } }}
              />
              <Typography sx={{ fontSize: '12px', color: '#9AA3B8', mt: 0.5 }}>
                {t('helpdesk.form.titleHint')}
              </Typography>
            </Box>

            {/* Category + Severity */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#001048', mb: 0.75 }}>
                  {t('helpdesk.form.category')} <span style={{ color: '#E53935' }}>*</span>
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={category}
                    onChange={handleCategoryChange}
                    disabled={isSubmitting}
                    sx={{ borderRadius: '8px', fontSize: '14px' }}
                  >
                    {CATEGORIES.map((c) => (
                      <MenuItem key={c.value} value={c.value}>
                        {c.emoji} {t(`helpdesk.category.${c.value}`)} — {c.desc}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#001048', mb: 0.75 }}>
                  {t('helpdesk.form.severity')}
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={priority}
                    onChange={handlePriorityChange}
                    disabled={isSubmitting}
                    sx={{ borderRadius: '8px', fontSize: '14px' }}
                  >
                    {PRIORITIES.map((p) => (
                      <MenuItem key={p.value} value={p.value}>
                        {p.emoji} {t(`helpdesk.priority.${p.value}`)}
                        {p.suffix}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography sx={{ fontSize: '12px', color: '#9AA3B8', mt: 0.5 }}>
                  {t('helpdesk.form.adminWillAdjust')}
                </Typography>
              </Grid>
            </Grid>

            {/* Description */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0.75
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#001048' }}>
                  {t('helpdesk.form.description')} <span style={{ color: '#E53935' }}>*</span>
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handlePolishDescription}
                  disabled={!description.trim() || polishingDescription || isSubmitting}
                  startIcon={
                    polishingDescription ? (
                      <CircularProgress size={12} color="inherit" />
                    ) : (
                      <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                    )
                  }
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.25,
                    borderColor: '#0274A1',
                    color: '#0274A1',
                    '&:hover': { bgcolor: '#EAF6FF', borderColor: '#01608a' },
                    '&.Mui-disabled': { borderColor: '#C8D0E0', color: '#9AA3B8' }
                  }}
                >
                  {polishingDescription
                    ? t('helpdesk.form.polishing')
                    : t('helpdesk.form.polishBtn')}
                </Button>
              </Box>
              <TextField
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                fullWidth
                multiline
                minRows={4}
                disabled={isSubmitting}
                placeholder={t('helpdesk.form.descriptionPlaceholder')}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '14px' } }}
              />
              <Typography sx={{ fontSize: '12px', color: '#9AA3B8', mt: 0.5 }}>
                {t('helpdesk.form.descriptionHint')}
              </Typography>
            </Box>

            {/* Steps to Reproduce */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#001048', mb: 0.75 }}>
                {t('helpdesk.form.stepsToReproduceLabel')}
              </Typography>
              {stepsToReproduce && (
                <Typography
                  sx={{ fontSize: '12px', color: '#697599', mb: 0.75, fontStyle: 'italic' }}
                >
                  {t('helpdesk.form.stepsToReproduceNote')}
                </Typography>
              )}
              <TextField
                value={stepsToReproduce}
                onChange={(e) => setStepsToReproduce(e.target.value)}
                fullWidth
                multiline
                minRows={5}
                disabled={isSubmitting}
                placeholder={t('helpdesk.form.stepsToReproducePlaceholder')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontFamily: 'monospace'
                  }
                }}
              />
            </Box>

            {/* Attachments */}
            <Box sx={{ mb: 4 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#001048', mb: 0.75 }}>
                {t('helpdesk.form.attachments')}
              </Typography>
              <Box
                onClick={() => !uploadingFile && fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragOver(true);
                }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleDrop}
                sx={{
                  border: `2px dashed ${isDragOver ? '#0274A1' : '#C8D0E0'}`,
                  borderRadius: '10px',
                  p: 3.5,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: isDragOver ? '#EAF6FF' : '#F8F9FB',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: '#0274A1', bgcolor: '#EAF6FF' }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  <AttachFileIcon sx={{ fontSize: 26, color: '#9AA3B8', mb: 0.5 }} />
                  <Typography fontSize={14} fontWeight={500} color="#697599">
                    {t('helpdesk.form.dropZoneLabel')}
                  </Typography>
                  <Typography fontSize={12} color="#9AA3B8">
                    {t('helpdesk.form.dropZoneSub')}
                  </Typography>
                </Box>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </Box>

              {uploadError && (
                <Typography sx={{ fontSize: '12px', color: '#E53935', mt: 0.75 }}>
                  {uploadError}
                </Typography>
              )}

              {(attachments.length > 0 || pendingFiles.length > 0) && (
                <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {attachments.map((att) => (
                    <Box
                      key={att.url}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2,
                        py: 1,
                        bgcolor: '#F3F4F7',
                        borderRadius: '8px',
                        border: '1px solid #E4E7EF'
                      }}
                    >
                      <AttachFileIcon sx={{ fontSize: 16, color: '#697599', flexShrink: 0 }} />
                      <Typography fontSize={13} color="#001048" sx={{ flex: 1 }} noWrap>
                        {att.name}
                      </Typography>
                      <Typography fontSize={12} color="#9AA3B8" sx={{ flexShrink: 0 }}>
                        {Math.round(att.sizeBytes / 1024)} KB
                      </Typography>
                      <CloseIcon
                        sx={{
                          fontSize: 16,
                          color: '#9AA3B8',
                          cursor: 'pointer',
                          flexShrink: 0,
                          '&:hover': { color: '#E53935' }
                        }}
                        onClick={() => handleRemoveAttachment(att.url)}
                      />
                    </Box>
                  ))}
                  {pendingFiles.map((file, idx) => (
                    <Box
                      key={`${file.name}-${idx}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2,
                        py: 1,
                        bgcolor: '#FFF9EC',
                        borderRadius: '8px',
                        border: '1px solid #FDE68A'
                      }}
                    >
                      <AttachFileIcon sx={{ fontSize: 16, color: '#D97706', flexShrink: 0 }} />
                      <Typography fontSize={13} color="#001048" sx={{ flex: 1 }} noWrap>
                        {file.name}
                      </Typography>
                      <Typography fontSize={12} color="#9AA3B8" sx={{ flexShrink: 0 }}>
                        {Math.round(file.size / 1024)} KB
                      </Typography>
                      <CloseIcon
                        sx={{
                          fontSize: 16,
                          color: '#9AA3B8',
                          cursor: 'pointer',
                          flexShrink: 0,
                          '&:hover': { color: '#E53935' }
                        }}
                        onClick={() => handleRemovePendingFile(idx)}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Footer */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
              <Button
                variant="outlined"
                onClick={onCancel}
                disabled={isSubmitting}
                sx={{
                  borderColor: '#C8D0E0',
                  color: '#697599',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: 3,
                  '&:hover': { borderColor: '#9AA3B8', bgcolor: '#F8F9FB' }
                }}
              >
                {t('helpdesk.form.cancelBtn')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || uploadingFile || !isValid}
                startIcon={
                  isSubmitting || uploadingFile ? (
                    <CircularProgress size={15} color="inherit" />
                  ) : undefined
                }
                sx={{
                  bgcolor: '#0274A1',
                  '&:hover': { bgcolor: '#01608a' },
                  '&.Mui-disabled': { bgcolor: '#C8D0E0', color: '#fff' },
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: 3
                }}
              >
                {isSubmitting || uploadingFile
                  ? t('helpdesk.form.submitting')
                  : t('helpdesk.form.submitBtn')}
              </Button>
            </Box>
          </Box>
        </Grid>

        {/* ── Right: Tips sidebar ── */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              bgcolor: '#FFFFFF',
              borderRadius: '14px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              p: 2.5,
              mb: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TipsAndUpdatesOutlinedIcon sx={{ color: '#F59E0B', fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#001048' }}>
                {t('helpdesk.form.tipsTitle')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {TIP_KEYS.map((key) => {
                const text = t(`helpdesk.form.${key}`);
                const dashIdx = text.indexOf(' — ');
                return (
                  <Box key={key} sx={{ display: 'flex', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        bgcolor: '#0274A1',
                        flexShrink: 0,
                        mt: '8px'
                      }}
                    />
                    <Typography fontSize={13} color="#4A5578" lineHeight={1.6}>
                      {dashIdx > -1 ? (
                        <>
                          <strong>{text.slice(0, dashIdx)}</strong>
                          {text.slice(dashIdx)}
                        </>
                      ) : (
                        text
                      )}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box
            sx={{
              bgcolor: '#EEF7FF',
              borderRadius: '12px',
              p: 2.5,
              border: '1px solid #C3E0F7'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AutoAwesomeIcon sx={{ fontSize: 17, color: '#0274A1' }} />
              <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#0274A1' }}>
                {t('helpdesk.form.aiAssistTitle')}
              </Typography>
            </Box>
            <Typography fontSize={13} color="#4A5578" lineHeight={1.6}>
              {t('helpdesk.form.aiAssistDesc')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NewTicketForm;
